import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Wordmark } from './primitives';

/**
 * FixedNav — top nav + drawer latéral (5 modules + Espace parent).
 *
 * Affichée sur toutes les pages authed (sauf /login et /parent*). Le drawer
 * s'ouvre via le burger, ferme via clic sur scrim ou navigation.
 */

interface NavItem {
  id: string;
  label: string;
  path: string;
  accent: string;
}

const ITEMS: NavItem[] = [
  { id: 'mission', label: 'Mission Control', path: '/', accent: 'var(--paper-50)' },
  { id: 'review', label: 'Révision', path: '/revision-cards', accent: 'var(--paper-50)' },
  { id: 'opening', label: 'Opening Lab', path: '/opening-lab', accent: 'var(--violet-500)' },
  { id: 'tactics', label: 'Tactics Dojo', path: '/tactics-dojo', accent: 'var(--cyan-500)' },
  { id: 'autopsy', label: 'Game Autopsy', path: '/game-autopsy', accent: 'var(--coral-500)' },
  { id: 'endgame', label: 'Endgame Forge', path: '/endgame-forge', accent: 'var(--amber-500)' },
  { id: 'practice', label: "L'Arène", path: '/practice-arena', accent: 'var(--orange-500)' },
  { id: 'stats', label: 'Stats', path: '/stats', accent: 'var(--acid-500)' },
];

export interface FixedNavUserChip {
  label: string;
  value: string;
  color?: string;
}

export function FixedNav({
  chips,
}: {
  chips?: FixedNavUserChip[];
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const close = (): void => setOpen(false);

  const activeId =
    location.pathname === '/'
      ? 'mission'
      : ITEMS.find((it) => it.path !== '/' && location.pathname.startsWith(it.path))?.id ?? null;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-30 h-14 bg-ink-900/85 backdrop-blur border-b border-ink-700 flex items-center px-4 gap-3">
        <button
          aria-label="Menu"
          onClick={() => setOpen((o) => !o)}
          className="w-8 h-8 inline-flex items-center justify-center rounded-md text-paper-50 hover:bg-ink-800 transition-all duration-150"
        >
          <span className="block w-4 h-px bg-current shadow-[0_-4px_0_currentColor,0_4px_0_currentColor]" />
        </button>
        <button
          onClick={() => navigate('/')}
          className="font-display text-base"
          aria-label="Mission Control"
        >
          <Wordmark />
        </button>
        <div className="flex-1" />
        {chips && chips.length > 0 && (
          <div className="hidden md:flex items-center gap-2 mr-2">
            {chips.slice(0, 4).map((chip, i) => (
              <div
                key={i}
                className="px-2 py-1 rounded-md border border-ink-700 bg-ink-800 font-mono text-[11px] text-paper-50 tabular-nums whitespace-nowrap"
                title={chip.label}
              >
                <span className="text-ink-300 mr-1.5 uppercase tracking-wider text-[10px]">{chip.label}</span>
                <span style={{ color: chip.color ?? 'var(--paper-50)' }}>{chip.value}</span>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={() => navigate('/parent')}
          className="text-caption uppercase font-mono text-ink-300 hover:text-paper-50 transition-all duration-150 px-2 py-1"
        >
          Parent
        </button>
      </header>

      {open && (
        <>
          <div
            onClick={close}
            className="fixed inset-0 z-40 bg-ink-900/70 backdrop-blur-sm animate-fade-in"
            aria-hidden="true"
          />
          <aside
            role="dialog"
            aria-label="Navigation"
            className="fixed top-0 left-0 bottom-0 z-50 w-72 bg-ink-900 border-r border-ink-700 p-5 flex flex-col gap-2 animate-card-enter"
          >
            <button
              onClick={close}
              aria-label="Fermer"
              className="self-end text-paper-50 hover:text-acid-500 transition-all duration-150 text-xl"
            >
              ✕
            </button>
            <div className="my-2">
              <Wordmark />
            </div>
            <nav className="flex flex-col gap-1 mt-4">
              {ITEMS.map((it) => {
                const active = activeId === it.id;
                return (
                  <button
                    key={it.id}
                    onClick={() => {
                      navigate(it.path);
                      close();
                    }}
                    className={`text-left px-3 py-2.5 rounded-lg font-display text-sm transition-all duration-150 ${
                      active
                        ? 'bg-ink-800 text-paper-50'
                        : 'text-ink-300 hover:bg-ink-800 hover:text-paper-50'
                    }`}
                    style={active ? { borderLeft: `3px solid ${it.accent}` } : undefined}
                  >
                    {it.label}
                  </button>
                );
              })}
              <div className="h-px bg-ink-700 my-3" />
              <button
                onClick={() => {
                  navigate('/parent');
                  close();
                }}
                className="text-left px-3 py-2.5 rounded-lg font-display text-sm text-ink-300 hover:bg-ink-800 hover:text-paper-50 transition-all duration-150"
              >
                Espace parent
              </button>
            </nav>
          </aside>
        </>
      )}
      {/* Spacer pour que le contenu ne passe pas sous la nav fixe */}
      <div className="h-14" aria-hidden="true" />
    </>
  );
}
